import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ContentProvider } from "@/lib/content";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import LegalPage from "@/pages/Legal";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const timers = [];
    if (hash) {
      // Sections animate in with a vertical offset, so the target position
      // settles only after entrance animations complete — scroll twice.
      [600, 1800].forEach((ms) => {
        timers.push(
          setTimeout(() => {
            const el = document.querySelector(hash);
            if (el) {
              if (window.__lenis) {
                window.__lenis.scrollTo(el, { offset: -110 });
              } else {
                el.scrollIntoView();
              }
            }
          }, ms)
        );
      });
    } else {
      const toTop = () => {
        if (window.__lenis) {
          window.__lenis.scrollTo(0, { immediate: true, force: true });
        } else {
          window.scrollTo(0, 0);
        }
      };
      toTop();
      [300, 1200].forEach((ms) => timers.push(setTimeout(toTop, ms)));
    }
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <ContentProvider>
        <BrowserRouter>
          <ScrollManager />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
              <Route path="/terms" element={<LegalPage docKey="terms" />} />
              <Route path="/disclaimer" element={<LegalPage docKey="disclaimer" />} />
            </Route>
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </BrowserRouter>
      </ContentProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#1A3624",
            color: "#F5F2EA",
            border: "1px solid rgba(197,168,128,0.5)",
            borderRadius: "9999px",
          },
        }}
      />
    </div>
  );
}

export default App;
